"use client";
import React, { useState, useMemo, useEffect, useRef } from "react";
import useResourceStore from "~/stores/resource-store";
import { SelectContent, SelectItem, SelectValue } from "components/ui/select";
import * as Select from "@radix-ui/react-select";
import { ResourceSortIcon } from "~/components/icons";
import { ResourceCard } from "../resource-card";
import { useSearchParams } from "next/navigation";

export const ResourceContentGrid = ({ allResourcesData }) => {
  const filters = useResourceStore((state) => state.filters);
  const [sortOption, setSortOption] = useState("Newest");
  const [visibleItems, setVisibleItems] = useState(9); // Start with 9 items
  const loaderRef = useRef(null);
  const searchParams = useSearchParams();

  // Handle sort option change
  const handleSortChange = (value) => {
    setSortOption(value);
  };

  // Function to filter resources based on active filters
  const filterResources = (resources) => {
    const search = searchParams.get("search");
    return resources.filter((resource) => {
      const matchesTopic =
        filters.topic.length === 0 ||
        resource?.blog_topics?.some((topic) =>
          filters.topic.includes(topic.title),
        );

      const matchesType =
        filters.type.length === 0 || filters.type.includes(resource.fileType);

      const matchesSearchQuery =
        !search ||
        resource.title.toLowerCase().includes(search.toLowerCase()) ||
        resource?.blog_topics?.some((topic) =>
          topic.title.toLowerCase().includes(search.toLowerCase()),
        ) ||
        resource.blog_category?.title
          ?.toLowerCase()
          ?.includes(search.toLowerCase())||
        resource.blog_authors?.some((author) =>
          author.Name.toLowerCase().includes(
            search.toLowerCase(),
          ),
        );

      return matchesTopic && matchesType && matchesSearchQuery;
    });
  };

  // Filtered and sorted resources
  const filteredResources = useMemo(
    () => filterResources(allResourcesData),
    [allResourcesData, filters],
  );

  const sortedResources = useMemo(
    () =>
      filteredResources.sort((a, b) => {
        const dateA = new Date(a.publishDate).getTime();
        const dateB = new Date(b.publishDate).getTime();
        return sortOption === "Newest" ? dateB - dateA : dateA - dateB;
      }),
    [filteredResources, sortOption],
  );

  // Infinite Scroll: Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleItems((prevVisibleItems) => prevVisibleItems + 9); // Load 9 more items
        }
      },
      { threshold: 1.0 },
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, []);

  return (
    <>
      {sortedResources.length > 0 ? (
        <>
          <div className="flex w-full items-center justify-end gap-6 py-6">
            <p className="font-p">
              Showing {sortedResources.length} of{" "}
              {sortedResources.length} results
            </p>
            <Select.Root onValueChange={handleSortChange}>
              <Select.Trigger
                aria-label="Sort Options"
                className="border-input focus:ring-ring flex w-[6rem] items-center justify-between whitespace-nowrap rounded-md border bg-[#414344] px-3 py-2 text-sm text-white shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
              >
                <SelectValue placeholder={"Sort By"} />
                <Select.Icon className="SelectIcon">
                  <ResourceSortIcon />
                </Select.Icon>
              </Select.Trigger>
              <SelectContent className="min-w-[6rem] bg-white">
                <SelectItem
                  className="border-b border-[#E2E2E2] text-center"
                  value="Newest"
                >
                  Newest
                </SelectItem>
                <SelectItem className="text-center" value="Oldest">
                  Oldest
                </SelectItem>
              </SelectContent>
            </Select.Root>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-5 lg:grid-cols-3 lg:gap-6">
            {sortedResources.slice(0, visibleItems).map((item, i) => (
              <div key={i}>
                <ResourceCard item={item} />
              </div>
            ))}
          </div>
          {visibleItems < sortedResources.length && (
            <div ref={loaderRef} className="h-20"></div>
          )}
        </>
      ) : (
        <>
          <div className=" w-full border-b border-black pb-6 pt-2">
            <p className="font-h6 pt-3 text-center uppercase lg:text-left">
              No results found matching your search
            </p>
          </div>
          <div className="py-6">
            <h5 className="font-h6 text-center font-light lg:text-left">
              Recommended resources
            </h5>
            <div className="grid grid-cols-1 gap-3 py-6 md:grid-cols-2 md:gap-5 lg:grid-cols-3 lg:gap-6">
              {allResourcesData.slice(0, 6).map((item, i) => (
                <div key={i}>
                  <ResourceCard item={item} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
};
